import { useMutation, useQueryClient } from '@tanstack/react-query';
// import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { inviteUsersAPI } from 'src/api';
import { ToastStyled } from 'src/components';
import type { MembershipRole } from 'src/types';

type Props = {
  invitees: { email: string; role: MembershipRole }[];
  setInvitees: (invitees: { email: string; role: MembershipRole }[]) => void;
  setEmailInputError: (
    error: { title: string; message: string } | null,
  ) => void;
};

export const useInviteUsers = ({
  setInvitees,
  invitees,
  setEmailInputError,
}: Props) => {
  // const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitees: { email: string; role: MembershipRole }[]) => {
      const {
        failedInvites,
        successInvites,
      }: {
        failedInvites: {
          email: string;
          role: MembershipRole;
        }[];
        successInvites: {
          email: string;
          role: MembershipRole;
          id: string | null;
        }[];
      } = await inviteUsersAPI(invitees);

      if (failedInvites.length > 0) {
        if (failedInvites.length === invitees.length) {
          setEmailInputError({
            title: 'Failed to Invite Users',
            message: 'Please check the email addresses and try again.',
          });
        } else {
          setEmailInputError({
            title: 'Failed to Invite Users',
            message:
              'Some users were not invited. Please check the email addresses and try again.',
          });

          // remove the invitees that did not fail to invite
          setInvitees(
            invitees.filter((invitee) => !failedInvites.includes(invitee)),
          );
        }
      } else {
        setInvitees([]);
        setEmailInputError(null);
      }

      return { failedInvites, successInvites };
    },
    onSuccess: (data) => {
      const successNum = data.successInvites.length;
      const failedNum = data.failedInvites.length;

      if (successNum > 0 && failedNum === 0) {
        toast.custom((toastBlock) => (
          <ToastStyled
            variant='success'
            handleClose={() => toast.dismiss(toastBlock)}
          >
            {successNum > 1 ? 'Emails have' : 'An email has'} been sent to the
            user{successNum > 1 ? 's' : ''}.
          </ToastStyled>
        ));
      } else if (successNum === 0 && failedNum > 0) {
        toast.custom((toastBlock) => (
          <ToastStyled
            variant='error'
            handleClose={() => toast.dismiss(toastBlock)}
          >
            Failed to invite user{failedNum > 1 ? 's' : ''}. Please try again.
          </ToastStyled>
        ));
      } else if (successNum > 0 && failedNum > 0) {
        toast.custom((toastBlock) => (
          <ToastStyled
            variant='warning'
            handleClose={() => toast.dismiss(toastBlock)}
          >
            Some user{failedNum > 1 ? 's' : ''} were not invited. Please try
            again.
          </ToastStyled>
        ));
      }

      // invalidate any queries that depend on the orgUsers query
      queryClient.invalidateQueries({
        queryKey: ['orgUsers'],
        type: 'all',
      });

      // refetch the currently active query
      queryClient.refetchQueries({
        queryKey: ['orgUsers'],
        type: 'active',
      });
    },
    onError: (_, data) => {
      // TODO - add error handling for a specific email failing, or all.
      toast.custom((toastBlock) => (
        <ToastStyled
          variant='error'
          handleClose={() => toast.dismiss(toastBlock)}
        >
          Failed to invite user{data.length > 1 ? 's' : ''}. Please try again.
        </ToastStyled>
      ));
    },
  });
};
